import React from 'react';
import { Component, Container, PuzzleCheckbox, PuzzleInput, PuzzleTooltip, HBox, WithTooltip, VBox } from "puzzle";
import {
  extendDataItem,
  filterBy,
  mapTree,
  TreeList,
  TreeListExpandChangeEvent,
  TreeListHeaderCellProps
} from "@progress/kendo-react-treelist";
import { CheckboxChangeEvent, Switch } from "@progress/kendo-react-inputs";
import * as _ from 'lodash';
import { NOTIFICATIONS_STATE, EMAIL, PREFERENCES_DATA } from "../Types";
import classNames from "classnames";
import { Keys } from "@progress/kendo-react-common";
import { orderBy } from '@progress/kendo-data-query';
import { getTableTdAccessibilityProps } from "../../util/Util";
import { Notification } from "@progress/kendo-react-notification";

type PROPS = {
  isClientView: boolean
  notifications: EMAIL[]
  sendEmailJustForMySubmissions: boolean
  mailNotificationsEnabled: boolean
}
type STATE = NOTIFICATIONS_STATE;
const DATA_ITEM_KEY: string = "classifier";

const subItemsField: string = "notifications";
const expandField: string = "expanded";

//Specific sort - keys send from server side not localization
const sortMap: any = {
  "Due data": 1,
  "Item status changes": 2,
  "Errors": 3,
  "Query Manager Notifications": 4
}

export class EmailPreference extends Component<PROPS, STATE> {
  tooltip: any;
  initNotifications: EMAIL[] = [];
  sendEmailJustForMySubmissionsInitValue = false;
  state = {
    data: [],
    expanded: [''],
    headerExpanded: false,
    filter: [],
    receiveNotsSwitch: this.props.sendEmailJustForMySubmissions || false
  };

  moduleDidMount = () => {
    const { notifications, sendEmailJustForMySubmissions, isClientView } = this.props;
    this.initNotifications = notifications;
    this.sendEmailJustForMySubmissionsInitValue = isClientView ? sendEmailJustForMySubmissions : false;
    const dataWithCategories = this.categorizeEmailPreference(notifications);
    this.setState({ data: dataWithCategories });
  };

  /**
   * Adding expand field
   *
   * @param dataTree
   */
  addExpandField = (dataTree: EMAIL[]) => {
    const expanded = this.state.expanded;
    const data = mapTree(dataTree, subItemsField, (item: EMAIL) => {
        return extendDataItem(item, subItemsField, {
          [expandField]: expanded.includes(item.classifier),
        })
      }
    );
    return data;
  };

  /**
   * Classify notifications to category
   *
   * @param notifications
   */
  categorizeEmailPreference = (notifications: EMAIL[]) => {
    let dataWithCategories = [];
    let nots = _.cloneDeep(notifications);
    nots = nots.map((current: EMAIL) => {
      const translated: any = this.translate(current.classifier);
      let localizedValue = typeof translated === 'object' ? translated.text : translated;
      return {
        ...current,
        localizedValue
      }
    });
    let newData = _.groupBy(nots, 'category');
    for (let i in newData) {
      if (newData.hasOwnProperty(i)) {
        dataWithCategories.push({
          notifications: orderBy(newData[i], [{
            field: 'sendMailSelected',
            dir: 'desc'
          }, {
            field: 'localizedValue',
            dir: 'asc'
          }]),
          category: i,
          order: sortMap[i],
          classifier: i,
          localizedValue: this.translate(i)
        });
      }
    }
    dataWithCategories = orderBy(dataWithCategories, [{
      field: 'order',
      dir: 'asc'
    }]);
    return dataWithCategories;
  };

  /**
   * Cell render for expandable column for the purpose of adding tooltip and localization
   *
   * @param cell
   * @param props
   */
  cellRender = (cell: any, props: any) => {
    if (props.expandable) {
      let children = [...cell.props.children];
      children[1] = this.translate(children[1]);
      if (typeof children[1] === 'object') {
        children[1] = (
          <span
            className={'k-d-inline-flex k-align-items-center'}
            key={props.id}>
            {children[1].text}
            <WithTooltip
              className={'k-d-flex'}
              context={this}
              title={children[1].tooltip}
              htmlTag={'span'}
            >
            <i title={children[1].tooltip} className={'tp-icon tp-info tp-color-link tp-pointable'} tabIndex={0} />
          </WithTooltip>
        </span>
        )
      }

      const localizedCell = React.cloneElement(
        cell,
        {
          ...cell.props,
          className: `${props.dataItem.notifications?.length ? 'tp-text-medium' : ''}`
        },
        children
      );
      return (
        <cell.type
          {...localizedCell.props}
        >
        </cell.type>
      );
    }
    return cell;
  }

  /**
   * Render for selection column
   *
   * @param props
   */
  cellSelectionRender = (props: any) => {
    const tdProps = getTableTdAccessibilityProps(props);
    const { dataItem, field = "" } = props;
    const cellData = dataItem[field];
    let selected = 0;
    if (dataItem?.notifications?.length) {
      selected = dataItem.notifications.filter((nots: EMAIL) => nots.sendMailSelected).length;
    }
    let tdCell = _.isBoolean(cellData) ? (<PuzzleCheckbox
      label={this.translate('emailTxt')}
      disabled={!this.props.mailNotificationsEnabled}
      checked={!!dataItem.sendMailSelected}
      onChange={this.handleSelectionChange.bind(this, dataItem)}
    />) : (
      <span className={'tp-color-link tp-text-semibold'}>{this.translate('selectedTxt', { selected: selected })}</span>)
    return (
      <td key={props.id + 'selection'}
          {...tdProps}
          className={'k-text-right'}>{tdCell}</td>);
  };

  /**
   * Returns preference data for save
   *
   * @param preferencesFormData
   */
  getDataForSave = (preferencesFormData: any) => {
    const { isClientView } = this.props;
    preferencesFormData.updatedNotifications = this.getUngroupedNotifications();
    preferencesFormData.sendEmailJustForMySubmissions = isClientView ? this.state.receiveNotsSwitch : false;
    return preferencesFormData;
  };

  /**
   * Braking groups into basic (array w/ objects) notifications
   */
  getUngroupedNotifications = () => {
    const { data } = this.state;
    let current: EMAIL;
    let item = data.length;
    let notifications: any[] = [];
    let clearedData;
    while (item--) {
      current = data[item];
      if (current.notifications) {
        clearedData = current.notifications.map((notification: EMAIL) => _.omit(notification, 'localizedValue'))
        notifications = notifications.concat(clearedData);
      }
    }
    return notifications
  };

  /**
   * Returns true if changes exists, false otherwise
   */
  hasUnsavedChanges = () => {
    const isEqual = _.isEqual(_.sortBy(this.initNotifications, 'classifier'), _.sortBy(this.getUngroupedNotifications(), 'classifier')) && (this.state.receiveNotsSwitch === this.sendEmailJustForMySubmissionsInitValue);
    return !isEqual;
  };

  /**
   * Handles filter change
   *
   * @param event
   */
  handleFilterChange = (event: any) => {
    const { data } = this.state;
    let expanded = data.reduce((acc: any[], currentNots: any) => {
      if (currentNots.notifications?.length) {
        acc.push(currentNots.category)
      }
      return acc;
    }, []);
    this.setState({
      headerExpanded: true,
      expanded,
      filter: [{
        field: "localizedValue",
        operator: "contains",
        value: event.value
      }]
    });
  };

  /**
   *  Handles selection change on data items
   *  Calls selection change from parent component
   *  and also marks data item selected on grid
   *
   * @param dataItem
   * @param event
   */
  handleSelectionChange = (dataItem: any, event: CheckboxChangeEvent) => {
    const { classifier } = dataItem;
    const isSelected = event.value;
    const stateData = _.cloneDeep(this.state.data)
    const newData = stateData.map((currentMail: EMAIL) => {
      if (currentMail.notifications) {
        currentMail.notifications.map((nots: EMAIL) => {
          if (nots.classifier === classifier) {
            nots.sendMailSelected = isSelected;
          }
          return nots;
        })
      }
      return currentMail;
    });
    this.setState({ data: newData })
  };

  /**
   * Handles switch change
   *
   * @param event
   */
  handleSwitchChange = (event: any) => {
    this.setState({
      receiveNotsSwitch: event.value
    })
  };

  /**
   * Header cell render - adding expand/collapse icon
   *
   * @param props
   */
  headerCellRender = (props: TreeListHeaderCellProps) => {
    const { headerExpanded } = this.state;
    const headerIconClass = classNames(
      'tp-pointable tp-icon',
      {
        'tp-collapse-list': headerExpanded,
        'tp-expand-list': !headerExpanded
      }
    )

    return (<Container alignItemsCenter hBox>
      <i className={headerIconClass}
         tabIndex={0}
         onClick={this.onHeaderIconClick} onKeyDown={this.onHeaderIconKeyDown} /><span
      className={'k-link tp-text-semibold'}>{props.title}</span>
    </Container>)
  };

  /**
   * Handles expand change
   *
   * @param e
   */
  onExpandChange = (e: TreeListExpandChangeEvent) => {
    this.setState({
      expanded: e.value
        ? this.state.expanded.filter((category: string) => category !== e.dataItem.category)
        : [...this.state.expanded, e.dataItem.category],
    });
  };

  /**
   * Handle header icon click - expand/collapse
   */
  onHeaderIconClick = () => {
    const { headerExpanded, data } = this.state;
    let expanded = [''];
    if (!headerExpanded) {
      expanded = data.reduce((acc: any[], currentNots: any) => {
        if (currentNots.notifications?.length) {
          acc.push(currentNots.category)
        }
        return acc;
      }, [])
    }
    this.setState({
      headerExpanded: !headerExpanded,
      expanded
    });
  };

  /**
   * Handles key down on header icon click.
   *
   * @param event
   */
  onHeaderIconKeyDown = (event: KeyboardEvent | any) => {
    if (event.keyCode === Keys.enter) {
      event.preventDefault();
      this.onHeaderIconClick();
    }
  };

  /**
   * Filtering data for show (to support filter if exists)
   */
  processData = () => {
    let filteredData = filterBy(this.state.data, this.state.filter, subItemsField);
    return this.addExpandField(filteredData);
  };

  /**
   * Updates initNotifications after saving for the future comparison
   *
   * @param preferenceData
   */
  updateSavedEmailPreference = (preferenceData: PREFERENCES_DATA) => {
    const { isClientView } = this.props;
    this.initNotifications = preferenceData.notifications;
    this.sendEmailJustForMySubmissionsInitValue = isClientView ? preferenceData.sendEmailJustForMySubmissions : false;
    const data = this.categorizeEmailPreference(preferenceData.notifications);
    this.setState({ data, receiveNotsSwitch: this.sendEmailJustForMySubmissionsInitValue })
  };

  render() {
    const { receiveNotsSwitch } = this.state;
    const { mailNotificationsEnabled } = this.props;
    return (
      <VBox flex1 gap={3}>
        {this.props.isClientView && <Container
          alignItemsCenter
          hBox
          className={'tp-switcher-with-label'}
        >
          <Switch
            name={'autoRefresh'}
            onChange={this.handleSwitchChange}
            checked={receiveNotsSwitch}
          />
          <label>
            {this.translate('receiveNotificationsTxt', { className: 'tp-text-semibold' })}
          </label>
        </Container>}
        <PuzzleInput
          label={this.translate('searchEmailPreferences')}
          labelClassName={'sr-only'}
          id='filterEmail'
          name='filterEmail'
          onChange={this.handleFilterChange}
          placeholder={this.translate('search')}
          searchField
        />
        <TreeList
          cellRender={this.cellRender}
          columns={[
            {
              field: "classifier",
              title: this.translate('notificationName'),
              width: "80%",
              expandable: true,
              headerCell: this.headerCellRender
            },
            {
              field: "sendMailSelected",
              title: this.translate('emailTxt'),
              width: "20%",
              cell: this.cellSelectionRender
            }
          ]}
          data={this.processData()}
          dataItemKey={DATA_ITEM_KEY}
          expandField={expandField}
          filter={this.state.filter}
          navigatable={true}
          onExpandChange={this.onExpandChange}
          subItemsField={subItemsField}
        />
        {!mailNotificationsEnabled && <HBox justifyContentEnd>
          <Notification
            className={'tp-informational '}
            type={{ style: 'warning', icon: true }}
          >
            {this.translate('disabledNotificationsBadge')}
          </Notification>
        </HBox>}
        <PuzzleTooltip
          ref={(el: any) => this.tooltip = el}
          anchorElement="target"
          openDelay={1000}
          position={'right'}
        />
      </VBox>
    );
  }
}

export default EmailPreference;
